import {
  Box,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  styled,
} from '@mui/material'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useSettings from '../../hooks/useSettings'
import { supportedLanguages, type SupportedLanguage } from '../../i18n'
import { FlagEmoji } from './FlagEmoji'

const StyledIconButton = styled(IconButton)({
  color: 'inherit',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const FlagIconContainer = styled(Box)({
  minWidth: 40,
  paddingRight: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

export default function LanguageSelector() {
  const { t, i18n } = useTranslation()
  const { setLanguage } = useSettings()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleLanguageSelect = useCallback(
    (languageCode: SupportedLanguage) => {
      setLanguage(languageCode)
      handleClose()
    },
    [setLanguage, handleClose]
  )

  const currentLanguage = useMemo(() => {
    return (
      supportedLanguages.find((lang) => lang.code === i18n.language) ||
      supportedLanguages[0]
    )
  }, [i18n.language])

  return (
    <Box>
      <Tooltip title={t('languageSelector.label')}>
        <StyledIconButton
          onClick={handleClick}
          size='small'
          aria-controls={open ? 'language-menu' : undefined}
          aria-haspopup='true'
          aria-expanded={open ? 'true' : undefined}
        >
          <FlagEmoji languageCode={currentLanguage.code} />
        </StyledIconButton>
      </Tooltip>
      <Menu
        id='language-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'language-button',
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {supportedLanguages.map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => handleLanguageSelect(language.code)}
            selected={language.code === currentLanguage.code}
          >
            <FlagIconContainer>
              <FlagEmoji languageCode={language.code} size={24} />
            </FlagIconContainer>
            <ListItemText>{language.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}
